import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "@/hooks/use-toast";
import { useOwnClientId } from "@/hooks/useOwnClientId";

const AiInsightPreview = () => {
  const { ownClientId, resolved } = useOwnClientId("AiInsightPreview");
  const [loading, setLoading] = useState(true);
  const [insight, setInsight] = useState<string | null>(null);
  const [generatedAt, setGeneratedAt] = useState<string | null>(null);
  const [generating, setGenerating] = useState(false);
  const [open, setOpen] = useState(false);

  const loadInsight = async () => {
    if (!ownClientId) {
      setLoading(false);
      return;
    }
    console.log("[AiInsightPreview] using client_id:", ownClientId);
    const { data } = await supabase
      .from("reports")
      .select("ai_insight, ai_insight_generated_at")
      .eq("client_id", ownClientId)
      .not("ai_insight", "is", null)
      .order("ai_insight_generated_at", { ascending: false })
      .limit(1);
    if (data && data.length > 0) {
      const ageMs = data[0].ai_insight_generated_at
        ? Date.now() - new Date(data[0].ai_insight_generated_at).getTime()
        : Infinity;
      if (ageMs < 7 * 24 * 60 * 60 * 1000) {
        setInsight(data[0].ai_insight);
        setGeneratedAt(data[0].ai_insight_generated_at);
      } else {
        setInsight(null);
        setGeneratedAt(data[0].ai_insight_generated_at);
      }
    } else {
      setInsight(null);
      setGeneratedAt(null);
    }
    setLoading(false);
  };

  useEffect(() => {
    if (resolved) loadInsight();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ownClientId, resolved]);

  const handleGenerate = async () => {
    if (!ownClientId) return;
    const { blockIfDemo } = await import("@/lib/demoMode");
    if (blockIfDemo("Generating AI insight")) return;
    console.log("[AiInsightPreview] using client_id:", ownClientId);
    setGenerating(true);
    try {
      // Ensure a recent weekly report exists, then generate the insight
      const { error: reportErr } = await supabase.functions.invoke("generate-weekly-report", {
        body: { client_id: ownClientId },
      });
      if (reportErr) console.warn("[AiInsightPreview] weekly report:", reportErr);

      const { data, error } = await supabase.functions.invoke("generate-ai-insight", {
        body: { client_id: ownClientId },
      });
      if (error || (data as any)?.error) {
        throw new Error((data as any)?.error || error?.message || "Failed to generate insight");
      }
      await loadInsight();
      toast({ title: "✨ Your AI insight is ready" });
      setOpen(true);
    } catch (err: any) {
      toast({ title: "Couldn't generate insight", description: err.message, variant: "destructive" });
    } finally {
      setGenerating(false);
    }
  };

  const previewText = (() => {
    if (!insight) return "";
    const sentences = insight.replace(/\s+/g, " ").trim().match(/[^.!?]+[.!?]+/g) || [insight];
    return sentences.slice(0, 2).join(" ").trim();
  })();

  if (loading) return null;

  const wrapperStyle: React.CSSProperties = {
    position: "relative",
    borderRadius: 14,
    padding: 1,
    marginBottom: 16,
    background:
      "linear-gradient(135deg, hsl(280 90% 65% / 0.55), hsl(220 90% 60% / 0.35) 45%, hsl(320 85% 65% / 0.5))",
    boxShadow: "0 14px 40px hsl(280 80% 50% / 0.14)",
  };

  const innerStyle: React.CSSProperties = {
    borderRadius: 13,
    padding: "12px 14px",
    background:
      "linear-gradient(135deg, hsl(var(--card) / 0.45) 0%, hsl(var(--card) / 0.35) 60%, hsl(280 60% 50% / 0.08) 100%)",
    backdropFilter: "blur(12px) saturate(140%)",
    WebkitBackdropFilter: "blur(12px) saturate(140%)",
    position: "relative",
    overflow: "hidden",
  };

  const badge = (
    <span
      className="inline-flex items-center gap-1 rounded-full text-[10px] font-[800] uppercase tracking-[0.8px]"
      style={{
        padding: "4px 10px",
        background: "linear-gradient(135deg, hsl(280 90% 60%), hsl(320 85% 60%))",
        color: "white",
        boxShadow: "0 4px 14px hsl(280 80% 50% / 0.4)",
      }}
    >
      ✨ AI Insight
    </span>
  );

  if (!insight) {
    const lockedUntil = generatedAt
      ? new Date(new Date(generatedAt).getTime() + 7 * 24 * 60 * 60 * 1000)
      : null;
    const locked = lockedUntil ? lockedUntil.getTime() > Date.now() : false;
    const lockedLabel = lockedUntil
      ? lockedUntil.toLocaleDateString(undefined, { month: "short", day: "numeric" })
      : "";
    return (
      <div style={wrapperStyle}>
        <button
          type="button"
          onClick={handleGenerate}
          disabled={generating || locked}
          className="block w-full text-left transition-transform hover:-translate-y-[2px] disabled:cursor-not-allowed disabled:opacity-70"
          style={innerStyle}
        >
          <div
            className="pointer-events-none absolute"
            style={{
              top: -60, right: -40, width: 220, height: 220,
              background: "radial-gradient(circle, hsl(280 90% 60% / 0.18) 0%, transparent 70%)",
            }}
          />
          <div className="relative flex items-center gap-3">
            <div
              className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl text-[16px]"
              style={{
                background: "linear-gradient(135deg, hsl(280 90% 60% / 0.15), hsl(320 85% 60% / 0.15))",
                border: "1px solid hsl(280 80% 60% / 0.25)",
              }}
            >
              ✨
            </div>
            <div className="flex-1 min-w-0">
              <div className="mb-0.5 flex items-center gap-2">{badge}</div>
              <div className="text-[13px] font-[700] text-foreground">
                {generating
                  ? "Crafting your weekly insight…"
                  : locked
                  ? "Your weekly AI insight is locked"
                  : "Your weekly AI insight is ready"}
              </div>
              <div className="text-[11.5px] text-muted-foreground">
                {generating
                  ? "Reading your data and writing it in plain English."
                  : locked
                  ? `Next insight available ${lockedLabel}`
                  : "Click to generate your weekly SEO summary."}
              </div>
            </div>
            <div
              className="hidden md:flex h-8 items-center rounded-full px-3 text-[11px] font-[800] text-white"
              style={{
                background: "linear-gradient(135deg, hsl(280 90% 60%), hsl(320 85% 60%))",
                boxShadow: "0 6px 16px hsl(280 80% 50% / 0.3)",
              }}
            >
              {generating ? "Generating…" : locked ? `Next: ${lockedLabel}` : "Generate →"}
            </div>
          </div>
        </button>
      </div>
    );
  }

  return (
    <>
      <div style={wrapperStyle}>
        <div style={innerStyle}>
          <div
            className="pointer-events-none absolute"
            style={{
              top: -80, right: -60, width: 280, height: 280,
              background: "radial-gradient(circle, hsl(280 90% 60% / 0.18) 0%, transparent 70%)",
            }}
          />
          <div className="relative flex flex-col gap-2 md:flex-row md:items-start">
            <div
              className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl text-[16px]"
              style={{
                background: "linear-gradient(135deg, hsl(280 90% 60% / 0.15), hsl(320 85% 60% / 0.15))",
                border: "1px solid hsl(280 80% 60% / 0.25)",
              }}
            >
              ✨
            </div>
            <div className="flex-1 min-w-0">
              <div className="mb-1.5 flex flex-wrap items-center gap-2">
                {badge}
                {generatedAt && (
                  <span className="text-[10px] font-[600] uppercase tracking-[0.6px] text-muted-foreground">
                    This week's update
                  </span>
                )}
              </div>
              <p
                className="text-[13px] leading-[1.5] text-foreground"
                style={{ fontFamily: "'Plus Jakarta Sans', serif" }}
              >
                {previewText}
              </p>
              <button
                type="button"
                onClick={() => setOpen(true)}
                className="mt-2 inline-flex h-8 items-center rounded-full px-3 text-[11.5px] font-[800] text-white transition-transform hover:-translate-y-[1px]"
                style={{
                  background: "linear-gradient(135deg, hsl(280 90% 60%), hsl(320 85% 60%))",
                  boxShadow: "0 6px 16px hsl(280 80% 50% / 0.3)",
                }}
              >
                Read full insight →
              </button>
            </div>
          </div>
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <div className="mb-2 flex items-center gap-2">{badge}</div>
            <DialogTitle className="text-[22px] font-[800] tracking-[-0.02em]">
              Your Weekly AI Insight
            </DialogTitle>
          </DialogHeader>
          <div
            className="rounded-2xl p-5"
            style={{
              background:
                "linear-gradient(135deg, hsl(280 60% 50% / 0.06), hsl(320 60% 50% / 0.04))",
              border: "1px solid hsl(280 60% 60% / 0.18)",
            }}
          >
            <p
              className="whitespace-pre-wrap text-[15px] leading-[1.7] text-foreground"
              style={{ fontFamily: "'Plus Jakarta Sans', serif" }}
            >
              {insight}
            </p>
          </div>
          {generatedAt && (
            <div className="text-[11px] uppercase tracking-[0.6px] text-muted-foreground">
              Generated {new Date(generatedAt).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default AiInsightPreview;
